/**
 * Deduplicação de Purchase por transação + plataforma.
 * Uma vez disparado, o flag persiste 30 dias (localStorage + cookie),
 * para não reenviar ao recarregar /customer/:hash.
 */

export const PURCHASE_EVENT_TTL_MS = 30 * 24 * 60 * 60 * 1000
export const PURCHASE_TRACK_QUERY = 'unico_track_purchase'

export function pendingPurchaseStorageKey(transactionId) {
    return `unico_pending_purchase_${transactionId}`
}

export function authorizePurchaseDispatch(transactionId) {
    if (!transactionId || typeof sessionStorage === 'undefined') return
    try {
        sessionStorage.setItem(pendingPurchaseStorageKey(transactionId), '1')
    } catch (e) {
        // ignore
    }
}

export function hasPurchaseDispatchAuthorization(transactionId) {
    if (!transactionId) return false
    try {
        if (typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem(pendingPurchaseStorageKey(transactionId)) === '1') {
            return true
        }
    } catch (e) {
        // ignore
    }
    if (typeof window !== 'undefined') {
        try {
            const params = new URLSearchParams(window.location.search)
            if (params.get(PURCHASE_TRACK_QUERY) === '1') return true
        } catch (e) {
            // ignore
        }
    }
    return false
}

export function capturePurchaseDispatchAuthorization(transactionId) {
    if (!transactionId || typeof window === 'undefined') return
    try {
        const url = new URL(window.location.href)
        if (url.searchParams.get(PURCHASE_TRACK_QUERY) !== '1') return
        authorizePurchaseDispatch(transactionId)
        url.searchParams.delete(PURCHASE_TRACK_QUERY)
        const qs = url.searchParams.toString()
        const next = url.pathname + (qs ? `?${qs}` : '') + url.hash
        window.history.replaceState({}, '', next)
    } catch (e) {
        // ignore
    }
}

export function consumePurchaseDispatchAuthorization(transactionId) {
    if (!transactionId) return
    try {
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(pendingPurchaseStorageKey(transactionId))
        }
    } catch (e) {
        // ignore
    }
    if (typeof window === 'undefined' || !window.history?.replaceState) return
    try {
        const url = new URL(window.location.href)
        if (!url.searchParams.has(PURCHASE_TRACK_QUERY)) return
        url.searchParams.delete(PURCHASE_TRACK_QUERY)
        const qs = url.searchParams.toString()
        window.history.replaceState({}, '', url.pathname + (qs ? `?${qs}` : '') + url.hash)
    } catch (e) {
        // ignore
    }
}

export function appendPurchaseTrackQuery(url) {
    if (!url) return url
    try {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost'
        const parsed = new URL(url, origin)
        parsed.searchParams.set(PURCHASE_TRACK_QUERY, '1')
        return parsed.toString()
    } catch (e) {
        const separator = String(url).includes('?') ? '&' : '?'
        return `${url}${separator}${PURCHASE_TRACK_QUERY}=1`
    }
}

export function getPurchaseEventStorageKey(transactionId, platformKey = null) {
    if (!transactionId) return ''
    return platformKey
        ? `purchase_event_fired_${transactionId}_${platformKey}`
        : `purchase_event_fired_${transactionId}`
}

export function parsePixelJsonArray(value) {
    if (Array.isArray(value)) return value
    if (value == null || value === '') return []
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch (e) {
            return []
        }
    }
    return []
}

export function pixelAllowsEvent(pixel, eventName) {
    const hits = parsePixelJsonArray(pixel?.event_hits)
    if (hits.length === 0) return true
    return hits.includes(eventName)
}

/**
 * Espelha o gate de Purchase do Facebook (hit_conditions).
 * paid = "Pagamentos aprovados"
 * generated_pix / generated_billet = tratar gerado como pago
 */
export function shouldFirePurchaseForHitConditions(transaction, conditions) {
    if (!transaction || !Array.isArray(conditions) || conditions.length === 0) {
        return false
    }

    const method = parseInt(transaction.payment_method, 10)
    const status = parseInt(transaction.payment_status, 10)
    if (Number.isNaN(method) || Number.isNaN(status)) {
        return false
    }

    if (method === 2) {
        if (conditions.includes('generated_billet')) return true
        return status === 3 && conditions.includes('paid')
    }

    if (method === 3) {
        if (conditions.includes('generated_pix')) return true
        return status === 3 && conditions.includes('paid')
    }

    if (method === 1) {
        return status === 3 && conditions.includes('paid')
    }

    return false
}

function readCookie(name) {
    if (typeof document === 'undefined' || !document.cookie) return null
    const parts = document.cookie.split(';')
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim()
        if (part.startsWith(name + '=')) {
            return decodeURIComponent(part.substring(name.length + 1))
        }
    }
    return null
}

function writeCookie(name, value, maxAgeSeconds) {
    if (typeof document === 'undefined') return
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`
}

function cookieNameForKey(lsKey) {
    return String(lsKey).replace(/[^a-zA-Z0-9_-]/g, '_')
}

function isTruthyFlag(value) {
    return value === 'true' || value === '1'
}

function isTimestampExpired(timestamp, ttlMs = PURCHASE_EVENT_TTL_MS) {
    if (!timestamp) return false
    const eventTime = parseInt(timestamp, 10)
    if (Number.isNaN(eventTime)) return false
    return Date.now() - eventTime > ttlMs
}

/**
 * A flag é estritamente por transação + plataforma. A chave genérica
 * `purchase_event_fired_<hash>` (legada) NÃO é consultada nem gravada: quando
 * qualquer plataforma a gravava, todas as outras passavam a se considerar
 * "já disparadas" — ex.: Facebook em "generated_pix" (dispara na geração do
 * PIX) bloqueava o Purchase do GTM configurado como "paid".
 */
export function isPurchaseEventFlagSet(transactionId, platformKey = null) {
    if (!transactionId) return false

    const lsKey = getPurchaseEventStorageKey(transactionId, platformKey)

    try {
        if (typeof localStorage !== 'undefined') {
            if (isTruthyFlag(localStorage.getItem(lsKey)) &&
                !isTimestampExpired(localStorage.getItem(`${lsKey}_timestamp`))) {
                return true
            }
        }

        if (typeof document !== 'undefined') {
            if (isTruthyFlag(readCookie(cookieNameForKey(lsKey)))) {
                return true
            }
        }
    } catch (e) {
        return false
    }

    return false
}

export function markPurchaseEventFlag(transactionId, platformKey = null) {
    if (!transactionId) return

    const lsKey = getPurchaseEventStorageKey(transactionId, platformKey)
    const now = Date.now().toString()
    const maxAgeSeconds = Math.floor(PURCHASE_EVENT_TTL_MS / 1000)

    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(lsKey, 'true')
            localStorage.setItem(`${lsKey}_timestamp`, now)
        }
        writeCookie(cookieNameForKey(lsKey), 'true', maxAgeSeconds)
    } catch (e) {
        // Safari privado / storage bloqueado: o cookie ainda pode ter sido gravado.
    }
}

export function cleanupExpiredPurchaseEventFlags(ttlMs = PURCHASE_EVENT_TTL_MS) {
    if (typeof localStorage === 'undefined') return

    try {
        const keys = Object.keys(localStorage)
        const purchaseEventKeys = keys.filter((key) => (
            key.startsWith('purchase_event_fired_') && !key.endsWith('_timestamp')
        ))

        purchaseEventKeys.forEach((key) => {
            const timestamp = localStorage.getItem(`${key}_timestamp`)
            if (isTimestampExpired(timestamp, ttlMs)) {
                localStorage.removeItem(key)
                localStorage.removeItem(`${key}_timestamp`)
            }
        })
    } catch (e) {
        // ignore
    }
}
